import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

// Parse numeric value from cell text
const parseNumericValue = (text) => {
    if (typeof text !== 'string') return null;

    const trimmed = text.trim();

    // Exclude date-like patterns (YYYY-MM-DD, YYYY-MM, MM/DD/YYYY, etc.)
    if (trimmed.match(/^\d{4}-\d{1,2}(-\d{1,2})?$/)) return null; // YYYY-MM-DD or YYYY-MM
    if (trimmed.match(/^\d{1,2}[-/]\d{1,2}[-/]\d{2,4}$/)) return null; // MM/DD/YYYY or DD-MM-YYYY
    if (trimmed.match(/^\d{4}\/\d{1,2}(\/\d{1,2})?$/)) return null; // YYYY/MM/DD or YYYY/MM

    // Remove common formatting: $, commas, %, whitespace
    let cleaned = trimmed
        .replace(/[$,\s]/g, '')
        .replace(/%$/, ''); // Remove trailing %

    // Handle parentheses as negative (accounting format)
    if (cleaned.match(/^\(.*\)$/)) {
        cleaned = '-' + cleaned.replace(/[()]/g, '');
    }

    // Handle K, M, B suffixes (thousands, millions, billions)
    const multipliers = { K: 1000, M: 1000000, B: 1000000000 };
    const suffix = cleaned.match(/[KMB]$/i);
    if (suffix) {
        cleaned = cleaned.slice(0, -1);
        const value = parseFloat(cleaned);
        return isNaN(value) ? null : value * multipliers[suffix[0].toUpperCase()];
    }

    const value = parseFloat(cleaned);
    return isNaN(value) ? null : value;
};

// Format number with commas
const formatNumber = (num, decimals = 2) => {
    return num.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
};

// Escape user-provided cell content for safe HTML clipboard payloads
const escapeHtml = (value) => {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};

// Quote TSV cells when needed (tabs/newlines/quotes/edge whitespace)
const formatTsvCell = (value) => {
    const text = String(value ?? '');
    const needsQuoting = /[\t\r\n"]/.test(text) || /^\s|\s$/.test(text);
    if (!needsQuoting) return text;
    return `"${text.replace(/"/g, '""')}"`;
};

const MarkdownTable = ({ children, onWidthOverflow, filename }) => {
    const tableRef = useRef(null);
    const containerRef = useRef(null);
    const [scrollIndicators, setScrollIndicators] = useState({ left: false, right: false });
    const stickyHeaderBackground = '#f5f7fa';
    const stickyHeaderSelectedBackground = '#dfe9f8';
    const stickyHeaderShadow = 'inset 0 -1px 0 rgba(0, 0, 0, 0.14), 0 2px 4px rgba(15, 23, 42, 0.08)';

    // Cell selection state
    const [selectionStart, setSelectionStart] = useState(null); // { row, col }
    const [selectionEnd, setSelectionEnd] = useState(null); // { row, col }
    const [isSelecting, setIsSelecting] = useState(false);
    const [activeCell, setActiveCell] = useState(null); // { row, col } - the focused cell
    const [tableDimensions, setTableDimensions] = useState({ rows: 0, cols: 0 });

    // Check if table overflows container and trigger callback
    useEffect(() => {
        if (tableRef.current && onWidthOverflow) {
            const table = tableRef.current;
            const container = table.parentElement; // Paper element

            // Small delay to ensure rendering is complete
            const checkOverflow = () => {
                if (table.scrollWidth > container.clientWidth) {
                    onWidthOverflow();
                }
            };

            // Check immediately and on resize
            checkOverflow();
            window.addEventListener('resize', checkOverflow);

            return () => {
                window.removeEventListener('resize', checkOverflow);
            };
        }
    }, [onWidthOverflow]);

    // Handle scroll indicators
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const updateScrollIndicators = () => {
            const { scrollLeft, scrollWidth, clientWidth } = container;
            const maxScroll = scrollWidth - clientWidth;

            setScrollIndicators({
                left: scrollLeft > 10, // Show left indicator if scrolled right
                right: scrollLeft < maxScroll - 10 // Show right indicator if not at right edge
            });
        };

        // Check initially and on scroll
        updateScrollIndicators();
        container.addEventListener('scroll', updateScrollIndicators);
        window.addEventListener('resize', updateScrollIndicators);

        return () => {
            container.removeEventListener('scroll', updateScrollIndicators);
            window.removeEventListener('resize', updateScrollIndicators);
        };
    }, []);

    // Calculate table dimensions from DOM
    useEffect(() => {
        if (tableRef.current) {
            const cells = tableRef.current.querySelectorAll('td, th');
            let maxRow = 0;
            let maxCol = 0;

            cells.forEach((cell) => {
                const row = parseInt(cell.getAttribute('data-row'));
                const col = parseInt(cell.getAttribute('data-col'));
                if (!isNaN(row) && !isNaN(col)) {
                    maxRow = Math.max(maxRow, row);
                    maxCol = Math.max(maxCol, col);
                }
            });

            setTableDimensions({ rows: maxRow + 1, cols: maxCol + 1 });
        }
    }, [children]);

    // Mouse event handlers for cell selection
    const handleCellMouseDown = useCallback((row, col) => {
        setSelectionStart({ row, col });
        setSelectionEnd({ row, col });
        setActiveCell({ row, col });
        setIsSelecting(true);
        // Focus container to enable keyboard navigation
        containerRef.current?.focus();
    }, []);

    const handleCellMouseEnter = useCallback((row, col) => {
        if (isSelecting) {
            setSelectionEnd({ row, col });
            setActiveCell({ row, col });
        }
    }, [isSelecting]);

    const handleMouseUp = useCallback(() => {
        setIsSelecting(false);
    }, []);

    const clearSelection = useCallback(() => {
        setSelectionStart(null);
        setSelectionEnd(null);
        setActiveCell(null);
        setIsSelecting(false);
    }, []);

    const getSelectedCellMatrix = useCallback(() => {
        if (!selectionStart || !selectionEnd || !tableRef.current) {
            return [];
        }

        const minRow = Math.min(selectionStart.row, selectionEnd.row);
        const maxRow = Math.max(selectionStart.row, selectionEnd.row);
        const minCol = Math.min(selectionStart.col, selectionEnd.col);
        const maxCol = Math.max(selectionStart.col, selectionEnd.col);
        const cellMap = new Map();

        tableRef.current.querySelectorAll('td, th').forEach((cell) => {
            const row = parseInt(cell.getAttribute('data-row'), 10);
            const col = parseInt(cell.getAttribute('data-col'), 10);

            if (Number.isNaN(row) || Number.isNaN(col)) {
                return;
            }

            if (row < minRow || row > maxRow || col < minCol || col > maxCol) {
                return;
            }

            cellMap.set(`${row},${col}`, (cell.textContent || '').trim());
        });

        const selectedRows = [];
        for (let row = minRow; row <= maxRow; row += 1) {
            const values = [];
            for (let col = minCol; col <= maxCol; col += 1) {
                values.push(cellMap.get(`${row},${col}`) || '');
            }
            selectedRows.push(values);
        }

        return selectedRows;
    }, [selectionStart, selectionEnd]);

    const getSelectedClipboardPayload = useCallback(() => {
        const selectedMatrix = getSelectedCellMatrix();
        if (!selectedMatrix.length) {
            return null;
        }

        // Use CRLF for maximum Excel compatibility on Windows; Sheets accepts it too.
        const plainText = selectedMatrix
            .map((row) => row.map(formatTsvCell).join('\t'))
            .join('\r\n');

        const htmlRows = selectedMatrix
            .map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell).replace(/\r?\n/g, '<br>')}</td>`).join('')}</tr>`)
            .join('');
        const htmlTable = `<table><tbody>${htmlRows}</tbody></table>`;

        return { plainText, htmlTable };
    }, [getSelectedCellMatrix]);

    const copySelectedCells = useCallback(async () => {
        const payload = getSelectedClipboardPayload();
        if (!payload) return;

        const { plainText, htmlTable } = payload;

        try {
            if (navigator.clipboard?.write && typeof window !== 'undefined' && typeof window.ClipboardItem !== 'undefined') {
                await navigator.clipboard.write([
                    new window.ClipboardItem({
                        'text/plain': new Blob([plainText], { type: 'text/plain' }),
                        'text/html': new Blob([htmlTable], { type: 'text/html' }),
                    }),
                ]);
                return;
            }
        } catch (_error) {
            // Fall through to plain-text clipboard fallback below
        }

        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(plainText);
                return;
            }
        } catch (_error) {
            // Fall through to execCommand fallback
        }

        const textarea = document.createElement('textarea');
        textarea.value = plainText;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        textarea.style.pointerEvents = 'none';
        textarea.style.top = '-1000px';
        textarea.style.left = '-1000px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
    }, [getSelectedClipboardPayload]);

    // Intercept native copy so keyboard/context-menu copy gets spreadsheet-friendly formats.
    useEffect(() => {
        const handleCopy = (e) => {
            if (!containerRef.current?.contains(document.activeElement)) return;
            if (!selectionStart || !selectionEnd) return;

            const payload = getSelectedClipboardPayload();
            if (!payload) return;

            const { plainText, htmlTable } = payload;
            if (!e.clipboardData) return;

            e.preventDefault();
            e.clipboardData.setData('text/plain', plainText);
            e.clipboardData.setData('text/html', htmlTable);
            try {
                e.clipboardData.setData('text/tab-separated-values', plainText);
            } catch (_error) {
                // Optional MIME type; safe to ignore when unsupported.
            }
        };

        document.addEventListener('copy', handleCopy);
        return () => document.removeEventListener('copy', handleCopy);
    }, [selectionStart, selectionEnd, getSelectedClipboardPayload]);

    // Handle mouse up globally to catch releases outside table
    useEffect(() => {
        document.addEventListener('mouseup', handleMouseUp);
        return () => document.removeEventListener('mouseup', handleMouseUp);
    }, [handleMouseUp]);

    // Find the edge cell in a given direction (for Cmd/Ctrl + Arrow)
    // Mimics Google Sheets behavior:
    // - If in a data region, jump to the last cell before empty
    // - If at edge of data region, jump to next data region
    // - If no more data, jump to table edge
    const findEdgeCell = useCallback((fromRow, fromCol, direction) => {
        if (!tableRef.current) return { row: fromRow, col: fromCol };

        const cells = tableRef.current.querySelectorAll('td, th');
        const cellMap = new Map();

        // Build a map of all cells
        cells.forEach((cell) => {
            const row = parseInt(cell.getAttribute('data-row'));
            const col = parseInt(cell.getAttribute('data-col'));
            const text = cell.textContent?.trim();
            if (!isNaN(row) && !isNaN(col)) {
                cellMap.set(`${row},${col}`, { row, col, isEmpty: !text });
            }
        });

        const currentCell = cellMap.get(`${fromRow},${fromCol}`);
        const isCurrentEmpty = currentCell?.isEmpty ?? true;

        let targetRow = fromRow;
        let targetCol = fromCol;

        if (direction === 'up') {
            let foundDataRegion = false;
            for (let r = fromRow - 1; r >= 0; r--) {
                const cell = cellMap.get(`${r},${fromCol}`);
                if (!cell) continue;

                if (!isCurrentEmpty) {
                    // We're in a data region - find the edge (last non-empty before empty)
                    if (cell.isEmpty) {
                        targetRow = r + 1;
                        break;
                    }
                    targetRow = r; // Keep going until we hit empty or edge
                } else {
                    // We're in empty space - find the next data region
                    if (!cell.isEmpty) {
                        foundDataRegion = true;
                        targetRow = r;
                    } else if (foundDataRegion) {
                        // Found edge of data region
                        targetRow = r + 1;
                        break;
                    }
                }
            }
        } else if (direction === 'down') {
            let foundDataRegion = false;
            for (let r = fromRow + 1; r < tableDimensions.rows; r++) {
                const cell = cellMap.get(`${r},${fromCol}`);
                if (!cell) continue;

                if (!isCurrentEmpty) {
                    // We're in a data region - find the edge
                    if (cell.isEmpty) {
                        targetRow = r - 1;
                        break;
                    }
                    targetRow = r;
                } else {
                    // We're in empty space - find the next data region
                    if (!cell.isEmpty) {
                        foundDataRegion = true;
                        targetRow = r;
                    } else if (foundDataRegion) {
                        targetRow = r - 1;
                        break;
                    }
                }
            }
        } else if (direction === 'left') {
            let foundDataRegion = false;
            for (let c = fromCol - 1; c >= 0; c--) {
                const cell = cellMap.get(`${fromRow},${c}`);
                if (!cell) continue;

                if (!isCurrentEmpty) {
                    if (cell.isEmpty) {
                        targetCol = c + 1;
                        break;
                    }
                    targetCol = c;
                } else {
                    if (!cell.isEmpty) {
                        foundDataRegion = true;
                        targetCol = c;
                    } else if (foundDataRegion) {
                        targetCol = c + 1;
                        break;
                    }
                }
            }
        } else if (direction === 'right') {
            let foundDataRegion = false;
            for (let c = fromCol + 1; c < tableDimensions.cols; c++) {
                const cell = cellMap.get(`${fromRow},${c}`);
                if (!cell) continue;

                if (!isCurrentEmpty) {
                    if (cell.isEmpty) {
                        targetCol = c - 1;
                        break;
                    }
                    targetCol = c;
                } else {
                    if (!cell.isEmpty) {
                        foundDataRegion = true;
                        targetCol = c;
                    } else if (foundDataRegion) {
                        targetCol = c - 1;
                        break;
                    }
                }
            }
        }

        return { row: targetRow, col: targetCol };
    }, [tableDimensions]);

    // Scroll active cell into view when it changes
    useEffect(() => {
        if (!activeCell || !tableRef.current || !containerRef.current) return;

        const cell = tableRef.current.querySelector(
            `[data-row="${activeCell.row}"][data-col="${activeCell.col}"]`
        );

        if (cell) {
            cell.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest'
            });
        }
    }, [activeCell]);

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Only handle keyboard events when table container is focused
            if (!containerRef.current?.contains(document.activeElement)) return;

            // Cmd/Ctrl + C copies the current selection.
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'c') {
                if (selectionStart && selectionEnd) {
                    e.preventDefault();
                    copySelectedCells();
                }
                return;
            }

            // Escape clears selection
            if (e.key === 'Escape') {
                clearSelection();
                return;
            }

            // Arrow keys for navigation (only if we have an active cell)
            if (!activeCell) return;

            const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
            if (!arrowKeys.includes(e.key)) return;

            e.preventDefault(); // Prevent scrolling

            const { row, col } = activeCell;
            let newRow = row;
            let newCol = col;

            // Cmd/Ctrl + Arrow = jump to edge
            const isJump = e.metaKey || e.ctrlKey;
            const isExtend = e.shiftKey;

            if (isJump) {
                let direction = '';
                if (e.key === 'ArrowUp') direction = 'up';
                else if (e.key === 'ArrowDown') direction = 'down';
                else if (e.key === 'ArrowLeft') direction = 'left';
                else if (e.key === 'ArrowRight') direction = 'right';

                const edge = findEdgeCell(row, col, direction);
                newRow = edge.row;
                newCol = edge.col;
            } else {
                // Normal arrow navigation
                if (e.key === 'ArrowUp') newRow = Math.max(0, row - 1);
                else if (e.key === 'ArrowDown') newRow = Math.min(tableDimensions.rows - 1, row + 1);
                else if (e.key === 'ArrowLeft') newCol = Math.max(0, col - 1);
                else if (e.key === 'ArrowRight') newCol = Math.min(tableDimensions.cols - 1, col + 1);
            }

            // Shift extends selection, otherwise move selection
            if (isExtend) {
                // Extend selection from anchor to new position
                // Keep the anchor (selectionStart) but move the end
                setSelectionEnd({ row: newRow, col: newCol });
                setActiveCell({ row: newRow, col: newCol });
            } else {
                // Move selection to new cell (new anchor)
                setSelectionStart({ row: newRow, col: newCol });
                setSelectionEnd({ row: newRow, col: newCol });
                setActiveCell({ row: newRow, col: newCol });
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [
        activeCell,
        tableDimensions,
        clearSelection,
        findEdgeCell,
        selectionStart,
        selectionEnd,
        copySelectedCells,
    ]);

    // Calculate if a cell is in the selection range
    const isCellSelected = useCallback((row, col) => {
        if (!selectionStart || !selectionEnd) return false;

        const minRow = Math.min(selectionStart.row, selectionEnd.row);
        const maxRow = Math.max(selectionStart.row, selectionEnd.row);
        const minCol = Math.min(selectionStart.col, selectionEnd.col);
        const maxCol = Math.max(selectionStart.col, selectionEnd.col);

        return row >= minRow && row <= maxRow && col >= minCol && col <= maxCol;
    }, [selectionStart, selectionEnd]);

    // Extract table data from children
    const extractTableData = () => {
        const rows = [];
        
        // Helper function to extract text from any node
        const extractText = (node) => {
            if (typeof node === 'string') return node;
            if (Array.isArray(node)) return node.map(extractText).join('');
            if (node?.props?.children) return extractText(node.props.children);
            return '';
        };

        // Process the table structure
        React.Children.forEach(children, (child) => {
            if (child?.type === 'thead') {
                React.Children.forEach(child.props.children, (tr) => {
                    if (tr?.type === 'tr') {
                        const headerRow = [];
                        React.Children.forEach(tr.props.children, (th) => {
                            headerRow.push(extractText(th.props.children));
                        });
                        rows.push(headerRow);
                    }
                });
            } else if (child?.type === 'tbody') {
                React.Children.forEach(child.props.children, (tr) => {
                    if (tr?.type === 'tr') {
                        const dataRow = [];
                        React.Children.forEach(tr.props.children, (td) => {
                            dataRow.push(extractText(td.props.children));
                        });
                        rows.push(dataRow);
                    }
                });
            }
        });

        return rows;
    };

    // Calculate aggregate statistics for selected cells
    const calculateAggregates = useCallback(() => {
        if (!selectionStart || !selectionEnd || !tableRef.current) {
            return null;
        }

        const minRow = Math.min(selectionStart.row, selectionEnd.row);
        const maxRow = Math.max(selectionStart.row, selectionEnd.row);
        const minCol = Math.min(selectionStart.col, selectionEnd.col);
        const maxCol = Math.max(selectionStart.col, selectionEnd.col);

        const selectedCells = [];
        const numericValues = [];

        // Gather cell values from DOM
        const cells = tableRef.current.querySelectorAll('td, th');
        cells.forEach((cell) => {
            const row = parseInt(cell.getAttribute('data-row'));
            const col = parseInt(cell.getAttribute('data-col'));

            if (row >= minRow && row <= maxRow && col >= minCol && col <= maxCol) {
                const text = cell.textContent;
                selectedCells.push(text);

                const numValue = parseNumericValue(text);
                if (numValue !== null) {
                    numericValues.push(numValue);
                }
            }
        });

        const totalCells = selectedCells.length;
        const numericCells = numericValues.length;

        if (numericCells === 0) {
            return {
                totalCells,
                numericCells,
                sum: null,
                average: null,
            };
        }

        const sum = numericValues.reduce((acc, val) => acc + val, 0);
        const average = sum / numericCells;

        return {
            totalCells,
            numericCells,
            sum,
            average,
        };
    }, [selectionStart, selectionEnd]);

    const aggregates = calculateAggregates();

    const exportToCSV = () => {
        const tableData = extractTableData();

        // Convert to CSV format
        const csvContent = tableData
            .map(row =>
                row.map(cell => {
                    // Escape quotes and wrap in quotes if contains comma, newline, or quotes
                    const cellStr = String(cell);
                    if (cellStr.includes(',') || cellStr.includes('\n') || cellStr.includes('"')) {
                        return `"${cellStr.replace(/"/g, '""')}"`;
                    }
                    return cellStr;
                }).join(',')
            )
            .join('\n');

        // Create descriptive filename
        let csvFilename;
        if (filename) {
            // Sanitize filename: lowercase, replace spaces/special chars with hyphens
            const sanitized = filename
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
            const dateStr = new Date().toISOString().slice(0, 10);
            csvFilename = `${sanitized}_${dateStr}.csv`;
        } else {
            csvFilename = `table_export_${new Date().toISOString().slice(0, 10)}.csv`;
        }

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', csvFilename);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const childElements = React.Children.toArray(children);
    const headerSection = childElements.find((child) => child?.type === 'thead');
    const headerRowCount = headerSection
        ? React.Children.count(headerSection.props?.children)
        : 0;

    return (
        <Box sx={{ position: 'relative', my: 2 }}>
            <Box sx={{ position: 'relative' }}>
                {/* Left scroll indicator */}
                <Box
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 40,
                        background: 'linear-gradient(to right, rgba(255,255,255,0.95), rgba(255,255,255,0))',
                        pointerEvents: 'none',
                        zIndex: 2,
                        opacity: scrollIndicators.left ? 1 : 0,
                        transition: 'opacity 0.3s ease',
                        borderTopLeftRadius: 4,
                        borderBottomLeftRadius: 4
                    }}
                />
                {/* Right scroll indicator */}
                <Box
                    sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: 40,
                        background: 'linear-gradient(to left, rgba(255,255,255,0.95), rgba(255,255,255,0))',
                        pointerEvents: 'none',
                        zIndex: 2,
                        opacity: scrollIndicators.right ? 1 : 0,
                        transition: 'opacity 0.3s ease',
                        borderTopRightRadius: 4,
                        borderBottomRightRadius: 4
                    }}
                />
                <Paper
                    ref={containerRef}
                    elevation={0}
                    tabIndex={0}
                    sx={{
                        overflow: 'auto',
                        maxHeight: '600px', // Reasonable height limit (~30 rows visible)
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        backgroundColor: '#fff',
                        userSelect: 'none', // Prevent text selection during drag
                        '&:focus': {
                            outline: 'none',
                        },
                    }}
                    onMouseDown={(e) => {
                        // Click on container (not cell) clears selection
                        if (e.target === e.currentTarget || e.target.tagName === 'TABLE') {
                            clearSelection();
                        }
                        // Focus the container so it can receive keyboard events
                        containerRef.current?.focus();
                    }}
                >
                    <table
                        ref={tableRef}
                        style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                        }}
                    >
                        {childElements.map((child) => {
                            if (!child) return null;

                            // Process thead or tbody
                            const isHeader = child.type === 'thead';
                            const rowOffset = isHeader ? 0 : headerRowCount;
                            const sectionStyle = isHeader ? {
                                ...child.props.style,
                                backgroundColor: stickyHeaderBackground,
                            } : child.props.style;

                            return React.cloneElement(child, {
                                style: sectionStyle,
                                children: React.Children.map(child.props.children, (row, rowIndex) => {
                                    if (!row || row.type !== 'tr') return row;

                                    const actualRowIndex = rowOffset + rowIndex;

                                    return React.cloneElement(row, {
                                        children: React.Children.map(row.props.children, (cell, colIndex) => {
                                            if (!cell) return null;

                                            const isSelected = isCellSelected(actualRowIndex, colIndex);
                                            const isActive = activeCell?.row === actualRowIndex && activeCell?.col === colIndex;
                                            const baseCellBackground = cell.props.style?.backgroundColor;

                                            const cellStyle = {
                                                ...cell.props.style,
                                                backgroundColor: isSelected
                                                    ? (isHeader ? stickyHeaderSelectedBackground : 'rgba(25, 118, 210, 0.12)')
                                                    : (isHeader ? (baseCellBackground || stickyHeaderBackground) : baseCellBackground),
                                                cursor: 'cell',
                                                transition: 'background-color 0.1s ease, box-shadow 0.1s ease',
                                                position: isHeader ? 'sticky' : 'relative',
                                                top: isHeader ? 0 : 'auto',
                                                boxShadow: isActive
                                                    ? isHeader
                                                        ? `inset 0 0 0 2px #1976d2, ${stickyHeaderShadow}`
                                                        : 'inset 0 0 0 2px #1976d2'
                                                    : (isHeader ? stickyHeaderShadow : 'none'),
                                                outline: 'none',
                                                backgroundClip: 'padding-box',
                                                zIndex: isHeader ? 13 : 'auto',
                                            };

                                            return React.cloneElement(cell, {
                                                'data-row': actualRowIndex,
                                                'data-col': colIndex,
                                                onMouseDown: (e) => {
                                                    e.preventDefault();
                                                    handleCellMouseDown(actualRowIndex, colIndex);
                                                },
                                                onMouseEnter: () => {
                                                    handleCellMouseEnter(actualRowIndex, colIndex);
                                                },
                                                style: cellStyle,
                                            });
                                        }),
                                    });
                                }),
                            });
                        })}
                    </table>
                </Paper>
            </Box>

            {/* Bottom Row: Aggregate Statistics + Export CSV */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 1,
                }}
            >
                {/* Aggregate Statistics Panel */}
                {aggregates && aggregates.totalCells > 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            padding: '6px 12px',
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            borderRadius: '6px',
                            fontSize: '0.8125rem',
                            color: 'rgba(0, 0, 0, 0.87)',
                        }}
                    >
                        <Typography
                            component="span"
                            sx={{
                                fontWeight: 600,
                                color: 'rgba(0, 0, 0, 0.6)',
                                fontSize: '0.8125rem',
                            }}
                        >
                            Selection
                        </Typography>
                        <Typography
                            component="span"
                            sx={{ fontSize: '0.8125rem' }}
                        >
                            {aggregates.totalCells} cell{aggregates.totalCells !== 1 ? 's' : ''}
                            {aggregates.numericCells > 0 && (
                                <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                                    {' '}({aggregates.numericCells} numeric)
                                </span>
                            )}
                        </Typography>
                        {aggregates.sum !== null && (
                            <>
                                <Typography
                                    component="span"
                                    sx={{ fontSize: '0.8125rem' }}
                                >
                                    <strong>Sum:</strong> {formatNumber(aggregates.sum)}
                                </Typography>
                                <Typography
                                    component="span"
                                    sx={{ fontSize: '0.8125rem' }}
                                >
                                    <strong>Avg:</strong> {formatNumber(aggregates.average)}
                                </Typography>
                            </>
                        )}
                    </Box>
                ) : (
                    <Box />
                )}

                {/* Export CSV Button */}
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={exportToCSV}
                    sx={{
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        py: 0.5,
                        px: 1.5
                    }}
                >
                    Export CSV
                </Button>
            </Box>
        </Box>
    );
};

export default MarkdownTable;
